import type { IngredientId } from './brandedTypes/ingredientId';
import type { Branded } from './brandedTypes/brand';
import type {
  BoardBasisCode,
  DestinationHotel,
  DetailsHotel,
  GuestRatingBreakdown,
  HotelAmenities,
  MainPolygon,
  OverviewActivity,
  OverviewHeartedHotel,
  OverviewHotel,
  Polygon,
} from './semboEndpoints';
import type {
  DecimalRangeTypeQL,
  StringRangeTypeQL,
} from './smartSearch/graphql_generated/graphql';
import type { DayTicketRate } from './smartSearch/responses/activityAvailabilityResponse';
import type { HotelBookableAndLockableOption } from './tripPlanner/bookableOption';

import type { GraphQL } from '.';

export type Dictionary<T> = Record<string, T>;

export interface Coordinate {
  latitude: number;
  longitude: number;
}

export const enum Landing {
  destination = 'destination',
  overview = 'overview',
  room = 'room',
}

export const enum StaticInfoType {
  Overview = 'overview',
  OverviewActivity = 'overviewActivity',
  OverviewHeartedHotel = 'overviewHeartedHotel',
  Destination = 'destination',
  Details = 'details',
  NameOnly = 'name',
  MainPolygonOnly = 'mainPolygonName',
  PositionOnly = 'position',
  NeighborhoodPolygonsOnly = 'neighborhoodPolygons',
  ActivitySuggestions = 'activitySuggestions',
}

export interface StaticInfoMap {
  [StaticInfoType.Overview]: OverviewDestinationStaticInfo;
  [StaticInfoType.OverviewActivity]: OverviewActivityStaticInfo;
  [StaticInfoType.OverviewHeartedHotel]: OverviewHeartedStaticInfo;
  [StaticInfoType.Destination]: DestinationStaticInfo;
  [StaticInfoType.Details]: DetailsStaticInfo;
  [StaticInfoType.NameOnly]: never;
  [StaticInfoType.MainPolygonOnly]: never;
  [StaticInfoType.PositionOnly]: never;
  [StaticInfoType.NeighborhoodPolygonsOnly]: never;
  [StaticInfoType.ActivitySuggestions]: never;
}

export interface OverviewStaticInfoResult {
  destinationStaticInfo: Dictionary<OverviewDestinationStaticInfo>;
  subIngredientsStaticInfo: Dictionary<OverviewActivityStaticInfo>;
  heartedHotelsStaticInfo: Dictionary<OverviewHeartedStaticInfo>;
}

export type OverviewStaticInfo =
  | OverviewDestinationStaticInfo
  | OverviewActivityStaticInfo
  | OverviewHeartedStaticInfo;

export type OverviewDestinationStaticInfo = OverviewHotel & {
  ingredientId: IngredientId;
  isHearted?: boolean;
  numberOfNights?: number;
  amenities?: HotelAmenities;
  guestRatings?: GuestRatingBreakdown;
};

export type OverviewActivityStaticInfo = OverviewActivity & {
  ingredientId: IngredientId;
  numberOfNights?: number;
};

export type OverviewHeartedStaticInfo = OverviewHeartedHotel & {
  ingredientId: IngredientId;
  polygons: Polygon[];
  isHearted?: boolean;
  numberOfNights?: number;
};

export type DestinationStaticInfo = DestinationHotel & {
  ingredientId: IngredientId;
  polygons: Polygon[];
  isHearted?: boolean;
  numberOfNights?: number;
};

export type DestinationProduct = DestinationStaticInfo & {
  totalPrice?: Money;
  grossPrice?: Money;
  boardBasis?: BoardBasisCode[];
};

export type DetailsStaticInfo = DetailsHotel & {
  ingredientId: IngredientId;
  tagIconUrls: string[];
  polygons: Polygon[];
  isHearted?: boolean;
  numberOfNights?: number;
  mainPolygon?: MainPolygon;
  guestRatings?: GuestRatingBreakdown;
};

export interface Money {
  amount: number;
  currency: string;
  currencySymbol: string;
}

export interface ContentApiImage {
  imageService: string;
  imageName: string;
}

export type SmartSearchImageUrl = Branded<string, 'SmartSearchImageUrl'>;

export enum IngredientType {
  OwnStay = 'OwnStay',
  Hotel = 'Hotel',
  Activity = 'Ticket',
  Flight = 'Flight',
  Ferry = 'Ferry',
  Transport = 'Transport',
  OwnTransport = 'OwnTransport',
}

// Backwards Compatibility
export const enum OldIngredientType {
  OwnStay = 'OWN_STAY',
  Hotel = 'HOTEL',
  Activity = 'TICKET',
  Flight = 'FLIGHT',
  Ferry = 'FERRY',
  Transport = 'TRANSPORT',
}

export const enum TripType {
  Return = 'Return',
  OneWay = 'One-way',
}
export const enum MyPositionType {
  Start = 'start',
  End = 'end',
}

export enum AvailabilityOrder {
  None = 'None',
  Cheapest = 'Cheapest',
  Popular = 'Popular',
}

export type RecipeMetaData = GraphQL.MetadataRequestQL;

export type StringRangeType = Partial<NonNullableProperties<StringRangeTypeQL>>;
export type DecimalRangeType = Partial<
  NonNullableProperties<DecimalRangeTypeQL>
>;

export type StaticInfoWithAvailability = (
  | OverviewStaticInfo
  | DestinationStaticInfo
) & {
  totalPrice?: Money;
};

export interface DetailedDestinationProduct {
  productCode: string;
  staticInfo:
    | DetailsStaticInfo
    | OverviewDestinationStaticInfo
    | DestinationStaticInfo;
  availability: DayTicketRate[] | HotelBookableAndLockableOption[];
  isAvailabilityLoading: boolean;
  isHearted: boolean;
  isInItinerary: boolean;
  numberOfNights: number;
  parentIngredientId: IngredientId;
  isAlternativeProduct: boolean;
}

export interface DynamicDetailedDestinationProduct {
  availability: HotelBookableAndLockableOption[];
}

export interface StaticDetailedDestinationProduct {
  productCode: string;
  staticInfo:
    | DetailsStaticInfo
    | OverviewDestinationStaticInfo
    | DestinationStaticInfo;
  isAvailabilityLoading: boolean;
  isHearted: boolean;
  isInItinerary: boolean;
  numberOfNights: number;
  ingredientId: IngredientId;
}

export interface StaticDetailedActivityProduct
  extends StaticDetailedDestinationProduct {
  parentIngredientId: IngredientId;
}

export interface ActivityDetailedProduct extends DetailedDestinationProduct {
  availability: DayTicketRate[];
}

export interface AccommodationDetailedProduct
  extends DetailedDestinationProduct {
  availability: HotelBookableAndLockableOption[];
}

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export type Month = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface RoomNote {
  severity: number;
  text: string;
  fromDate: string | null;
  toDate: string | null;
}

export interface RoomPax {
  numberOfAdults: number;
  childAges?: number[];
  numberOfChildren?: number;
}
export type Pax = RoomPax[];

// Backwards Compatibility
type AdultGuest = null;
type ChildGuest = number;
type Guest = AdultGuest | ChildGuest;
export type OldRoomPax = Guest[];
export type OldPax = OldRoomPax[];

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
type oneToNine = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
type zeroToNine = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type YYYY = `20${zeroToNine}${zeroToNine}`;
type MM = `0${oneToNine}` | `1${0 | 1 | 2}`;
type DD = `${0}${oneToNine}` | `${1 | 2}${zeroToNine}` | `3${0 | 1}`;
// NOTE: 2024-12-20
export type DateOnlyString = `${YYYY}-${MM}-${DD}`;

export type URLString = `http${'' | 's'}://${string}.${string}.${string}`;

export const enum FilterTypeEvents {
  TextSearch = 'text_search',
}

export type NonNullableProperties<T, Keys extends keyof T = keyof T> = Omit<
  T,
  Keys
> & {
  [K in Keys]-?: NonNullable<T[K]>;
};
